import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.jsx';
import api from './services/api.js';
import { LocaleProvider } from './context/LocaleContext.jsx';
import AppErrorBoundary from './components/AppErrorBoundary.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import FeatureGate from './components/FeatureGate.jsx';
import { PLATFORM_ROLE_KEYS } from './config/roles.js';
import DefaultWebLayout from './layouts/_default-web-layout.jsx';
import DefaultWebLayout2otx06 from './layouts/_default-web-layout-2otx06.jsx';
import DefaultWebLayout1mnzqsb from './layouts/_default-web-layout-1mnzqsb.jsx';
import UsersGroupsRolesLayout from './layouts/_users-groups-roles-layout.jsx';
import AuthWebLayout from './layouts/auth-web-layout.jsx';
import {
  Unauthorized401,
  Forbidden403,
  NotFound404,
  ServerError500,
} from './pages/status/index.js';
import { defaultRoute, signInRoute, NOT_FOUND_ROUTE } from './config/menuData.js';
import './App.scss';

// Pages built from the design reference, each bound to the layout named in its
// scope record and keyed on its slug from src/config/menuData.js.
const SignInPage = lazy(() => import('./pages/sign-in/index.jsx'));
const LoraTrainingPage = lazy(() => import('./pages/lora-training/index.jsx'));
const BrandRulesPage = lazy(() => import('./pages/brand-rules/index.jsx'));
const ConsentRegisterPage = lazy(() => import('./pages/consent-register/index.jsx'));
const AppGalleryPage = lazy(() => import('./pages/app-gallery/index.jsx'));
const CreativeAppRunnerPage = lazy(() => import('./pages/creative-app-runner/index.jsx'));
const UsersRolesPage = lazy(() => import('./pages/users-roles/index.jsx'));
const ModelRegistryPage = lazy(() => import('./pages/model-registry/index.jsx'));
const ModelRegistrationWizardPage = lazy(
  () => import('./pages/model-registration-wizard/index.jsx')
);
const WorkerRuntimeHealthPage = lazy(() => import('./pages/worker-runtime-health/index.jsx'));
const OrchestrationCommandCenterPage = lazy(
  () => import('./pages/orchestration-command-center/index.jsx')
);
const CapabilityDescriptorEditorPage = lazy(
  () => import('./pages/capability-descriptor-editor/index.jsx')
);
const DynamicParameterPanelPage = lazy(() => import('./pages/dynamic-parameter-panel/index.jsx'));
const GenerationStudioPage = lazy(() => import('./pages/generation-studio/index.jsx'));
const ReelGeneratorPage = lazy(() => import('./pages/reel-generator/index.jsx'));
const EpisodesPage = lazy(() => import('./pages/episodes/index.jsx'));
const VoiceoverStudioPage = lazy(() => import('./pages/voiceover-studio/index.jsx'));
const TalkingAvatarPage = lazy(() => import('./pages/talking-avatar/index.jsx'));
const MediaLibraryPage = lazy(() => import('./pages/media-library/index.jsx'));
const SecurityPage = lazy(() => import('./pages/security/index.jsx'));
const ResultsVariantsPage = lazy(() => import('./pages/results-variants/index.jsx'));
const ComparisonViewPage = lazy(() => import('./pages/comparison-view/index.jsx'));
const AssetDetailLineagePage = lazy(() => import('./pages/asset-detail-lineage/index.jsx'));
const AssetVaultProjectsPage = lazy(() => import('./pages/asset-vault-projects/index.jsx'));
const WorkflowEditorPage = lazy(() => import('./pages/workflow-editor/index.jsx'));
const WorkflowTestHarnessPage = lazy(() => import('./pages/workflow-test-harness/index.jsx'));
const CreativeAppBuilderBindingEditorPage = lazy(
  () => import('./pages/creative-app-builder-binding-editor/index.jsx')
);
const ApiCredentialsPage = lazy(() => import('./pages/api-credentials/index.jsx'));
const ApiJobStatusPage = lazy(() => import('./pages/api-job-status/index.jsx'));
const RoutingPoliciesPage = lazy(() => import('./pages/routing-policies/index.jsx'));
const WorkspacesQuotasPage = lazy(() => import('./pages/workspaces-quotas/index.jsx'));
const UsageCostDashboardsPage = lazy(() => import('./pages/usage-cost-dashboards/index.jsx'));
const AuditSearchExportPage = lazy(() => import('./pages/audit-search-export/index.jsx'));
const BrandElementsPresetsPage = lazy(() => import('./pages/brand-elements-presets/index.jsx'));
const SceneBiblesPage = lazy(() => import('./pages/scene-bibles/index.jsx'));
const ArtStylesPage = lazy(() => import('./pages/art-styles/index.jsx'));
const ShowPackagesPage = lazy(() => import('./pages/show-packages/index.jsx'));
const WorkSurfacePage = lazy(() => import('./pages/work-surface/index.jsx'));
const ReviewApprovalQueuePage = lazy(() => import('./pages/review-approval-queue/index.jsx'));
const CreativeAssistantPanelPage = lazy(() => import('./pages/creative-assistant-panel/index.jsx'));
// My Approvals (inbox + request detail) and Settings → Approval policies —
// Episodes Plan 2 Task 12.
const ApprovalsPage = lazy(() => import('./pages/approvals/index.jsx'));
const ApprovalPoliciesPage = lazy(() => import('./pages/approval-policies/index.jsx'));

// Internal eval console. Not a product page: cross-tenant, staff-only, and
// absent from src/config/menuData.js on purpose so nothing in the app links to
// it. Lazy like the rest, which also keeps it out of the main bundle.
const EvalConsolePage = lazy(() => import('./pages/eval/index.jsx'));

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

/**
 * The canonical login route. Forwards to whatever `signInRoute()` resolves to —
 * now the real `/sign-in` page. Keeping /login stable means ProtectedRoute's
 * redirect and the layouts' log-in affordance never point at a dead route.
 */
function LoginRoute() {
  const location = useLocation();
  const target = signInRoute();

  if (target === NOT_FOUND_ROUTE) {
    return <NotFound404 />;
  }
  return <Navigate to={target} replace state={location.state} />;
}

/**
 * Landing route. DMI-23.01 makes the active work surface the default home,
 * honouring the user's SERVER-SIDE `landing_surface` preference (Addendum A
 * §4 — prefs follow the user across devices, so this is a read, not
 * localStorage). 'gallery' keeps the design's DefaultMenuItem (App Gallery);
 * anything else — including the backend default 'work' and any fetch
 * failure for an unauthenticated visitor — resolves the way it always did
 * for signed-out users: `defaultRoute()`, whose ProtectedRoute bounces to
 * sign-in.
 */
function IndexRoute() {
  const { isAuthenticated, loading } = useAuth();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (loading) return undefined;
    if (!isAuthenticated) {
      setTarget(defaultRoute());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const prefs = await api.request('/work-surface/prefs');
        if (!cancelled) {
          setTarget(prefs?.landingSurface === 'gallery' ? defaultRoute() : '/work-surface');
        }
      } catch {
        if (!cancelled) setTarget(defaultRoute());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, loading]);

  if (!target) return <Loader />;
  return target === NOT_FOUND_ROUTE ? <NotFound404 /> : <Navigate to={target} replace />;
}

function App() {
  return (
    <LocaleProvider>
      <AuthProvider>
      <BrowserRouter>
        <AppErrorBoundary>
          <Suspense fallback={<Loader />}>
            <Routes>
              {/* Anonymous entry point — auth-web-layout, deliberately OUTSIDE
                  every ProtectedRoute/RoleRoute group so it stays reachable.
                  The "already signed in → leave" gate lives INSIDE SignInPage
                  itself (not a wrapper route), because only SignInPage knows
                  whether an in-progress MFA/SSO challenge is what just flipped
                  isAuthenticated true — a wrapper here would redirect away
                  mid-challenge, before the user ever sees the MFA modal. */}
              <Route element={<AuthWebLayout />}>
                <Route path="sign-in" element={<SignInPage />} />
              </Route>

              {/* Internal eval console (Xamun engineering only).

                  Mounted here, bare: no layout element, no ProtectedRoute, no
                  RoleRoute. Not an oversight — every one of those wrappers is
                  built around the PRODUCT session (`token`, AuthContext, the
                  tenant's platform roles), and this console must be reachable
                  by a signed-out engineer and unreachable by a signed-in
                  customer administrator. Wrapping it in ProtectedRoute would
                  invert both.

                  Access is decided server-side by the staff allow-list behind
                  /api/eval/*; the page treats 401/403 as "not staff" and
                  refuses without explaining. Its session lives under
                  `evalToken`, deliberately not the product's `token`. */}
              <Route path="eval" element={<EvalConsolePage />} />

              {/* Platform-Administrator-only console, on its own design layout. */}
              <Route element={<UsersGroupsRolesLayout />}>
                <Route element={<RoleRoute allow={[PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR]} />}>
                  <Route path="users-roles" element={<UsersRolesPage />} />
                </Route>

                {/* Model Registration Wizard shares this design layout but has
                    its own, wider role set — reached only via the Model
                    Registry's "Add Model Endpoint" workflow step, not the main
                    nav. */}
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.MODEL_ADMINISTRATOR,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  <Route
                    path="model-registration-wizard"
                    element={<ModelRegistrationWizardPage />}
                  />

                  {/* Capability Descriptor Editor — reached via Model
                      Registry's "Edit Capability Schema" / "Open Capability
                      Schema Editor" (frontend/src/pages/model-registry/
                      links.js DESIGN_PAGE_ID.capabilitySchemaEditor = 73214,
                      registered in menuData.js's pages[]). */}
                  <Route
                    path="capability-descriptor-editor"
                    element={<CapabilityDescriptorEditorPage />}
                  />

                  {/* Dynamic Parameter Panel — reached via the Capability
                      Descriptor Editor's "Preview Dynamic Controls". Optional
                      :modelId falls back to the first published model. */}
                  <Route
                    path="dynamic-parameter-panel/:modelId?"
                    element={<DynamicParameterPanelPage />}
                  />
                </Route>

                {/* Results & Variants — reached via the Dynamic Parameter
                    Panel's "Generate Assets Button" (use case f94f75a9
                    "Generation Studio", workflow step 3). Gated to any
                    authenticated user (`ProtectedRoute`), not a `RoleRoute`:
                    `PLATFORM_ROLE_KEYS.CREATIVE_USER` exists as a key, but
                    `roles.js`'s `roleKeysOf` fallback has no path that ever
                    assigns it to a real session user yet (only the scaffold's
                    `role === 'admin'` maps onto Platform Administrator), so
                    gating on it would lock out every session — consistent
                    with how the sibling Generation Studio / Dynamic Parameter
                    Panel routes for this same use case are gated. Optional
                    :jobId falls back to the seeded current batch. */}
                <Route element={<ProtectedRoute />}>
                  <Route path="results-variants/:jobId?" element={<ResultsVariantsPage />} />
                </Route>

                {/* Creative Assistant Panel (page dc49a37b-…, SCR-11) — use case
                    47086259-…'s `has_page` edges omitted this screen even
                    though TC-39218-01 opens it (concern
                    9ac38edb-c324-4d3d-891c-0ace4d551e5e's linkage ruling: the
                    Test Case is the executable contract, so the screen it
                    names is built and routed here). Reached from Generation
                    Studio's "Open Creative Assistant Panel" toolbar action
                    (see pages/generation-studio/links.js). Gated to Creative
                    User + Senior Creative / Approver + Platform Administrator,
                    matching this page's own scope record's Permissions
                    block — not the main nav (no design page id in menuData,
                    same pattern as comparison-view / dynamic-parameter-panel
                    above). */}
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.CREATIVE_USER,
                        PLATFORM_ROLE_KEYS.APPROVER,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  <Route path="creative-assistant-panel" element={<CreativeAssistantPanelPage />} />
                </Route>
              </Route>

              {/* Worker & Runtime Health — its own design layout. Gated to
                  Model Administrator + Platform Administrator: the page's own
                  scope record says Platform-Administrator-only, but the
                  workflow ("Inspect Worker Health" from the Model Registration
                  Wizard) and TC-39225-01 both require the Model Administrator
                  to reach it too. See raised concern
                  e1eb2130-3348-4f69-b488-570e03de8119. */}
              <Route element={<DefaultWebLayout2otx06 />}>
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.MODEL_ADMINISTRATOR,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  <Route path="worker-runtime-health" element={<WorkerRuntimeHealthPage />} />
                  {/* Orchestration Command Center — live job queue, GPU worker
                      fleet, routing decision traces and cost meter over SSE
                      (brief §10 Routing, §11 Job Management). Same admin gate
                      as Worker & Runtime Health. */}
                  <Route
                    path="orchestration-command-center"
                    element={<OrchestrationCommandCenterPage />}
                  />
                </Route>

                {/* Credential & Secrets Management + Job Status / Queue Tray
                    (use case 4ac54323-…, Public API & Webhook Integration) —
                    the two human-admin monitoring/config surfaces around the
                    machine-to-machine `/api/v1/*` API. Platform-Administrator-
                    only, matching every other Administration-group page.
                    Workflow step 1: "API Credentials" -> "Monitor API Job
                    Queue" -> "API Job Status" (see
                    pages/api-credentials/links.js). */}
                <Route element={<RoleRoute allow={[PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR]} />}>
                  <Route path="api-credentials" element={<ApiCredentialsPage />} />
                  <Route path="api-job-status" element={<ApiJobStatusPage />} />
                  {/* Workspaces & Quotas (use case 61f77339-…) — Platform
                      Administrator only, per the page's own scope record.
                      Workflow: Routing Policies -> "Manage Workspace Quotas". */}
                  <Route path="workspaces-quotas" element={<WorkspacesQuotasPage />} />
                </Route>

                {/* Routing Policy Editor & Dry-Run (use case 61f77339-…) —
                    Model Administrator + Platform Administrator, per the
                    page's own scope record. */}
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.MODEL_ADMINISTRATOR,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  <Route path="routing-policies" element={<RoutingPoliciesPage />} />
                </Route>

                {/* Audit Search & Export (use case be4a90a6-…) — Auditor/
                    Compliance + Platform Administrator, read-only, per the
                    page's own scope record. TC-39230-02 requires a Creative
                    User to be denied (nav hidden + direct nav -> 403). */}
                <Route
                  element={
                    <RoleRoute
                      allow={[PLATFORM_ROLE_KEYS.AUDITOR, PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR]}
                    />
                  }
                >
                  <Route path="compliance-audit" element={<AuditSearchExportPage />} />
                </Route>
              </Route>

              {/* Comparison View Screen — its own design layout
                  (`_default-web-layout-1mnzqsb`, per its scope record's
                  `layout_file`). Reached only via Results & Variants' "Select
                  Variants for Comparison" (use case f7976ca2, workflow step
                  1), not the main nav — `menuData.js`'s `pages[]` registers it
                  with `pageId: null` for that reason. Gated to any
                  authenticated user (`ProtectedRoute`), matching
                  `results-variants` immediately above: `roleKeysOf`'s
                  fallback has no path today that grants a real session user
                  `PLATFORM_ROLE_KEYS.CREATIVE_USER`, so `RoleRoute
                  allow=[CREATIVE_USER]` would lock out every session. Reads
                  the selected variant ids from a `?variants=` query param
                  (see `pages/comparison-view/links.js`). */}
              <Route element={<DefaultWebLayout1mnzqsb />}>
                <Route element={<ProtectedRoute />}>
                  <Route path="comparison-view" element={<ComparisonViewPage />} />
                </Route>

                {/* Asset Detail & Lineage View Screen — same design layout
                    (`_default-web-layout-1mnzqsb`, per its scope record's
                    `layout_file`), shared with `asset-vault-projects` /
                    `brand-elements-presets`. Reached only via Asset Vault &
                    Projects' "Inspect Asset Lineage" action (use case
                    6073ba2f, workflow step 2), not the main nav —
                    `menuData.js`'s `pages[]` registers it with `pageId: null`
                    for that reason. Gated to Creative User + Platform
                    Administrator, matching `generation-studio` immediately
                    below (the same module's role gate is real for
                    `CREATIVE_USER` — see that route's comment). Optional
                    `:assetId` falls back to the design's showcased asset
                    (see `pages/asset-detail-lineage/links.js` /
                    `services/assetDetailLineageService.js`).

                    Asset Vault & Projects shares this same RoleRoute gate —
                    it is the Creative User's project workspace surface for
                    this module (use case 6073ba2f), registered in the main
                    nav (`menuData.js`'s "Asset Vault & Projects" item, page
                    id 73192). See `pages/asset-vault-projects/index.jsx`'s
                    header comment for the full role-gate rationale. */}
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.CREATIVE_USER,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  <Route
                    path="asset-detail-lineage/:assetId?"
                    element={<AssetDetailLineagePage />}
                  />
                  <Route path="asset-vault-projects" element={<AssetVaultProjectsPage />} />

                  {/* Brand Elements & Presets (use case 942f42d3-…) — reached
                      via App Gallery's "Open Brand & Presets Manager"
                      (workflow step 1, the page's only step). Same design
                      layout + role gate as its siblings above. */}
                  <Route path="brand-elements-presets" element={<BrandElementsPresetsPage />} />

                  {/* Scene Bibles (Addendum A DMI-21) — the Board 1 builder:
                      cast, set, VFX and the plate matrix under a versioned,
                      lockable bible. In the main nav ("Scene Bibles", Brand &
                      Identity group). Same design layout + role gate as its
                      siblings above: the bible is a creative-authoring object,
                      so the Creative User gate that governs
                      brand-elements-presets governs it too. */}
                  <Route path="scene-bibles" element={<SceneBiblesPage />} />

                  {/* Animation Styles — the locked looks a world is drawn in;
                      same creative-authoring gate as Scene Bibles. */}
                  <Route path="art-styles" element={<ArtStylesPage />} />

                  {/* Show Packages — the standing open/close + stings a
                      programme reuses byte-identical every episode. Beside
                      Scene Bibles in the nav and behind the same gate: like
                      a bible it is a creative-authoring continuity object
                      the Reel Generator pins. */}
                  <Route path="show-packages" element={<ShowPackagesPage />} />
                </Route>

                {/* Workflow Test Harness + Creative App Builder & Binding
                    Editor (use case 1287c5ff-…, workflow steps 1-2 from
                    Workflow Editor). Reached only via workflow, not the main
                    nav — same role gate as `/workflow-editor` above. Optional
                    `:workflowId` falls back to the first available workflow
                    (see each page's own data hook). */}
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.WORKFLOW_DEVELOPER,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  <Route
                    path="workflow-test-harness/:workflowId?"
                    element={<WorkflowTestHarnessPage />}
                  />
                  <Route
                    path="creative-app-builder-binding-editor/:workflowId?"
                    element={<CreativeAppBuilderBindingEditorPage />}
                  />
                </Route>

                {/* Usage & Cost Dashboards (use case 61f77339-…) — reached
                    only via Workspaces & Quotas' "View Usage Metrics" action,
                    not the main nav. Gated to Platform Administrator +
                    Auditor/Compliance (view-only), per the page's own scope
                    record. */}
                <Route
                  element={
                    <RoleRoute
                      allow={[PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR, PLATFORM_ROLE_KEYS.AUDITOR]}
                    />
                  }
                >
                  <Route path="usage-cost-dashboards" element={<UsageCostDashboardsPage />} />
                </Route>
              </Route>

              {/* The project's default web shell frames the app until a page
                  binds itself to a different design layout. */}
              <Route element={<DefaultWebLayout />}>
                <Route index element={<IndexRoute />} />
                <Route path="login" element={<LoginRoute />} />

                {/* Authenticated workspace pages. */}
                <Route element={<ProtectedRoute />}>
                  {/* My Work — the active work surface (Addendum A DMI-23).
                      Any authenticated user, no RoleRoute: every operator has
                      obligations; the role-sensitive approval SOURCE is gated
                      server-side (workSurface.service.js mirrors
                      review.routes.js's Approver gate). In the main nav
                      ("My Work", top) and the default landing target — see
                      IndexRoute above. */}
                  <Route path="work-surface" element={<WorkSurfacePage />} />
                  <Route path="app-gallery" element={<AppGalleryPage />} />
                  {/* The Creative App runner is a creative-user surface, NOT a
                      builder one: no Workflow Developer gate. It shows only the
                      app's exposed parameters, never the node graph. */}
                  <Route
                    path="creative-app-runner/:creativeAppId"
                    element={<CreativeAppRunnerPage />}
                  />
                  {/* Brand Rules — readable by any signed-in user (a creative
                      refused by a rule deserves to read the rule); the page
                      itself gates the create/toggle form to Platform
                      Administrator, matching brandRules.routes.js. */}
                  <Route path="brand-rules" element={<BrandRulesPage />} />
                  {/* Consent Register — readable by any signed-in user, for the
                      same reason Brand Rules is: a creative refused because a
                      presenter's release lapsed deserves to read that sentence
                      rather than guess. The page itself gates registering,
                      approving and revoking to Approver + Platform
                      Administrator, matching authorizedIdentity.routes.js. */}
                  <Route path="consent-register" element={<ConsentRegisterPage />} />
                  {/* My Approvals — inbox + request detail. Any authenticated
                      user: whether you have anything to decide is determined
                      server-side (eligibleUsersForStep), same pattern as My
                      Work above. */}
                  <Route path="approvals" element={<ApprovalsPage />} />
                  <Route path="approvals/:id" element={<ApprovalsPage />} />
                  {/* Episodes — the producer's running order. A PLAIN route
                      (wave 3, audit F4): an approver, auditor or Finance user
                      opening a request must be able to read the episode. The
                      server decides who may edit (GET returns canEdit; every
                      write is Owner/Editor-gated), the page renders read-only
                      otherwise, and creating one stays creative-role only. */}
                  <Route path="episodes" element={<EpisodesPage />} />
                  <Route path="episodes/:id" element={<EpisodesPage />} />
                  {/* Settings → Approval policies. Deliberately a PLAIN route,
                      not a RoleRoute: a workspace Owner (not a platform admin)
                      must be able to reach it (task-12 ruling). The 403 for
                      "neither platform admin nor Owner" is enforced and
                      messaged by the backend (approval.service.js
                      savePolicyVersion / a read that returns one), and
                      PolicyEditor shows that message in place rather than
                      the app redirecting to /403 client-side. */}
                  <Route path="approval-policies" element={<ApprovalPoliciesPage />} />
                </Route>

                {/* Model Registry — Model Administrator + Platform Administrator. */}
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.MODEL_ADMINISTRATOR,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  <Route path="model-registry" element={<ModelRegistryPage />} />
                </Route>

                {/* Workflow Editor — reached from the "Workflows" nav group
                    (menuData.js pageId 73203, use case 1287c5ff-…). Gated to
                    Workflow Developer + Platform Administrator, the role that
                    builds/tests/publishes visual workflow graphs. */}
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.WORKFLOW_DEVELOPER,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  {/* `:workflowId` opens the editor full-page for one workflow;
                      without it the page is the catalog. The deep-link code in
                      the page existed from the start, but the route never
                      carried the segment, so "Edit Canvas" could only render
                      the editor 2,000px below the table. */}
                  <Route path="workflow-editor/:workflowId?" element={<WorkflowEditorPage />} />
                </Route>

                {/* Generation Studio — reached via App Gallery's "Launch
                    Creative Studio" (use case f94f75a9, workflow step 1).
                    Gated to Creative User + Platform Administrator:
                    `PLATFORM_ROLE_KEYS.CREATIVE_USER` is a real, seeded
                    platform role (backend/seeds/03_users.js assigns it to
                    several accounts via `user_platform_roles`, read live by
                    `loadAuthContext`/`buildSessionUser` onto the session
                    user's `platformRoleKeys` — see
                    backend/src/security/authorizationContext.js,
                    backend/src/modules/auth/session.js), so `roleKeysOf`
                    resolves it correctly and this gate is real, not the
                    `role === 'admin'` fallback. */}
                <Route
                  element={
                    <RoleRoute
                      allow={[
                        PLATFORM_ROLE_KEYS.CREATIVE_USER,
                        PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR,
                      ]}
                    />
                  }
                >
                  <Route path="generation-studio" element={<GenerationStudioPage />} />
                  {/* Reel Generator (flagship) — the creative-production
                      orchestration: brief → script → storyboard → voiceover →
                      composed reel. Same creative gate as the Studio. */}
                  <Route path="reel-generator" element={<ReelGeneratorPage />} />
                  {/* Voiceover Studio — narration on its own. The Voiceover
                      tile used to route into the reel pipeline, which meant
                      producing a whole reel to get thirty seconds of speech. */}
                  <Route path="voiceover-studio" element={<VoiceoverStudioPage />} />
                  {/* Talking Avatar — the page works and shows the rendered
                      reads; only generating a new one is blocked, and the page
                      states why where the action is. Behind the runtime
                      FEATURE_TALKING_AVATAR flag (GET /api/brand): default on,
                      `off` hides page + nav for a demo. */}
                  <Route
                    path="talking-avatar"
                    element={<FeatureGate flag="talkingAvatar"><TalkingAvatarPage /></FeatureGate>}
                  />
                  {/* Media Library — generated assets separated by modality
                      (Image / Video / Audio / Motion Graphics). Same creative
                      gate as the Studio. */}
                  <Route path="media-library" element={<MediaLibraryPage />} />
                  {/* Every signed-in user manages their own authenticator. */}
                  <Route path="security" element={<SecurityPage />} />
                  {/* Characters — the model-sheet home for every character/
                      presenter, including the LoRA training flow. Canonical at
                      /characters; /lora-training kept as a redirect so old
                      links and bookmarks keep working. */}
                  <Route path="characters" element={<LoraTrainingPage />} />
                  <Route path="lora-training" element={<Navigate to="/characters" replace />} />
                </Route>

                {/* Review & Approval Queue (use case 3154b64a-…) — reached via
                    App Gallery's "Open Review Queue" pending-approvals widget
                    (menuData.js pageId 73200) and the "Review & Quality" nav
                    group. Gated to Senior Creative / Approver + Platform
                    Administrator, matching this page's scope record's
                    Permissions block and assetApproval.routes.js's existing
                    backend gate. */}
                <Route
                  element={
                    <RoleRoute
                      allow={[PLATFORM_ROLE_KEYS.APPROVER, PLATFORM_ROLE_KEYS.PLATFORM_ADMINISTRATOR]}
                    />
                  }
                >
                  <Route path="review-queue" element={<ReviewApprovalQueuePage />} />
                </Route>

                {/* Generic status pages. */}
                <Route path="401" element={<Unauthorized401 />} />
                <Route path="403" element={<Forbidden403 />} />
                <Route path="404" element={<NotFound404 />} />
                <Route path="500" element={<ServerError500 />} />

                {/* Catch-all — any unresolved path lands on 404. */}
                <Route path="*" element={<NotFound404 />} />
              </Route>
            </Routes>
          </Suspense>
        </AppErrorBoundary>
      </BrowserRouter>
      </AuthProvider>
    </LocaleProvider>
  );
}

export default App;
